import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAdminBlogStats } from "../../api/blog";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import Loader from "../../components/ui/Loader";
import {
  ArrowLeft,
  Heart,
  MessageCircle,
  BarChart3,
  TrendingUp,
  Search,
  Eye,
  Edit,
  PenTool,
  Sparkles,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { format } from "date-fns";

export default function AdminBlogAnalytics() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await getAdminBlogStats();
      setBlogs(data);
    } catch (error) {
      console.error("Failed to fetch blog stats", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBlogs = blogs.filter((b) =>
    b.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const topPost = blogs[0];

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen bg-background py-6 md:py-10 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-[30%] h-[30%] bg-accent/5 blur-[120px] -z-10" />
      <div className="absolute bottom-0 left-0 w-[30%] h-[30%] bg-blue-500/5 blur-[120px] -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/dashboard")}
            className="mb-8 hover:bg-muted"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Admin Dashboard
          </Button>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-primary to-accent shadow-xl shadow-accent/20">
                  <BarChart3 className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl md:text-5xl font-black tracking-tight text-foreground flex items-center gap-3">
                    Blog Analytics
                    <Badge variant="secondary" className="bg-accent/10 text-accent text-xs font-black">PRO VERSION</Badge>
                  </h1>
                  <p className="text-muted-foreground font-medium">Real-time performance metrics for your content ecosystem.</p>
                </div>
              </div>
            </div>

            <div className="relative w-full lg:w-96 group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <Input
                placeholder="Search across all publications..."
                className="pl-12 h-12 bg-card/50 backdrop-blur-md border-border rounded-2xl focus:ring-4 focus:ring-primary/10 transition-all font-medium"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Top Performer Card */}
        {topPost && !searchTerm && (
          <div className="mb-12 relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary to-accent rounded-[2.5rem] blur opacity-20 group-hover:opacity-30 transition duration-1000 group-hover:duration-200"></div>
            <Card className="relative overflow-hidden border-0 shadow-2xl rounded-[2.5rem] bg-card/80 backdrop-blur-xl">
              <div className="lg:flex">
                <div className="lg:w-1/3 h-72 lg:h-auto relative overflow-hidden">
                  {topPost.coverImage ? (
                    <img
                      src={topPost.coverImage}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      alt={topPost.title}
                    />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <PenTool className="w-16 h-16 text-muted-foreground/30" />
                    </div>
                  )}
                  <div className="absolute top-6 left-6">
                    <Badge className="bg-white/90 dark:bg-black/90 text-foreground dark:text-white border-0 font-black px-4 py-1.5 rounded-xl shadow-2xl backdrop-blur-md">
                      <TrendingUp className="w-4 h-4 mr-2 text-orange-500" /> TOP PERFORMER
                    </Badge>
                  </div>
                </div>
                <CardContent className="lg:w-2/3 p-8 lg:p-12 flex flex-col justify-center space-y-8">
                  <h2 className="text-3xl lg:text-5xl font-black text-foreground leading-[1.1]">
                    {topPost.title}
                  </h2>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10">
                      <p className="text-[10px] uppercase tracking-widest font-black text-muted-foreground mb-1">Impact Score</p>
                      <p className="text-2xl font-black text-primary">{Math.round(topPost.engagementScore)}</p>
                    </div>
                    <div className="p-4 rounded-2xl bg-primary/5 border border-primary/15">
                      <p className="text-[10px] uppercase tracking-widest font-black text-muted-foreground mb-1">Reach</p>
                      <p className="text-2xl font-black text-primary flex items-center gap-2"><Eye className="w-5 h-5" /> {topPost.views || 0}</p>
                    </div>
                    <div className="p-4 rounded-2xl bg-rose-500/5 border border-rose-500/10">
                      <p className="text-[10px] uppercase tracking-widest font-black text-muted-foreground mb-1">Approval</p>
                      <p className="text-2xl font-black text-rose-600 flex items-center gap-2"><Heart className="w-5 h-5" /> {topPost._count?.likes || 0}</p>
                    </div>
                    <div className="p-4 rounded-2xl bg-blue-500/5 border border-blue-500/10">
                      <p className="text-[10px] uppercase tracking-widest font-black text-muted-foreground mb-1">Dialogue</p>
                      <p className="text-2xl font-black text-blue-600 flex items-center gap-2"><MessageCircle className="w-5 h-5" /> {topPost._count?.comments || 0}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 pt-2">
                    <Button onClick={() => navigate(`/admin/blog-analytics/${topPost.id}`)} className="h-12 px-8 rounded-2xl font-black shadow-lg">
                      <BarChart3 className="w-4 h-4 mr-2" /> Extended Analytics
                    </Button>
                    <Button variant="outline" onClick={() => navigate(`/admin/blog/edit/${topPost.id}`)} className="h-12 px-8 rounded-2xl font-black">
                      <Edit className="w-4 h-4 mr-2" /> Modify Content
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        )}

        {/* List Section */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black text-foreground flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-accent" />
              Content Library
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {filteredBlogs.map((blog, index) => (
              <Card key={blog.id} className="group border border-border shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 bg-card/60 backdrop-blur-md rounded-2xl overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div className="flex items-center gap-6 flex-1 min-w-0">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black shadow-lg shrink-0 ${
                        index === 0 ? "bg-gradient-to-r from-yellow-400 to-amber-500" :
                        index === 1 ? "bg-gradient-to-r from-slate-300 to-slate-400" :
                        index === 2 ? "bg-gradient-to-r from-amber-600 to-amber-700" :
                        "bg-muted text-muted-foreground"
                      }`}>
                        #{index + 1}
                      </div>

                      <div className="w-16 h-16 rounded-2xl overflow-hidden shrink-0 shadow-lg group-hover:scale-105 transition-transform">
                        {blog.coverImage ? (
                          <img src={blog.coverImage} className="w-full h-full object-cover" alt="" />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground/30"><PenTool className="w-6 h-6" /></div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1 space-y-1">
                        <h3 className="text-lg font-black text-foreground group-hover:text-primary transition-colors truncate">{blog.title}</h3>
                        <p className="text-xs font-bold text-muted-foreground flex items-center gap-2 uppercase tracking-widest italic">
                          <span>{blog.author?.email}</span>
                          <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                          <span>{format(new Date(blog.createdAt), "MMM d, yyyy")}</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-6 lg:gap-12">
                      <div className="flex items-center gap-8">
                        <div className="text-center group/stat">
                          <div className="flex items-center gap-2 mb-1">
                            <Eye className="w-4 h-4 text-primary group-hover/stat:scale-110 transition-transform" />
                            <span className="font-black text-lg">{blog.views || 0}</span>
                          </div>
                          <span className="text-[10px] font-black uppercase tracking-tight text-muted-foreground/60">Reach</span>
                        </div>
                        <div className="text-center group/stat">
                          <div className="flex items-center gap-2 mb-1">
                            <Heart className="w-4 h-4 text-rose-500 group-hover/stat:scale-110 transition-transform" />
                            <span className="font-black text-lg">{blog._count?.likes || 0}</span>
                          </div>
                          <span className="text-[10px] font-black uppercase tracking-tight text-muted-foreground/60">Likes</span>
                        </div>
                        <div className="text-center group/stat">
                          <div className="flex items-center gap-2 mb-1">
                            <MessageCircle className="w-4 h-4 text-blue-500 group-hover/stat:scale-110 transition-transform" />
                            <span className="font-black text-lg">{blog._count?.comments || 0}</span>
                          </div>
                          <span className="text-[10px] font-black uppercase tracking-tight text-muted-foreground/60">Comments</span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button size="icon" variant="secondary" onClick={() => navigate(`/admin/blog/edit/${blog.id}`)} className="rounded-xl hover:bg-primary hover:text-white transition-all"><Edit className="w-4 h-4" /></Button>
                        <Button size="icon" variant="secondary" onClick={() => navigate(`/admin/blog-analytics/${blog.id}`)} className="rounded-xl hover:bg-accent hover:text-white transition-all"><BarChart3 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
